import { Controller } from "@hotwired/stimulus"

/**
 * 宝宝选择下拉组件
 *
 * 使用方法：
 * <div data-controller="baby-select" data-baby-select-url-value="/admin/ai/playground/babies">
 *   <input type="text" data-baby-select-target="input" placeholder="搜索或选择宝宝...">
 *   <div data-baby-select-target="dropdown"></div>
 *   <div data-baby-select-target="selectedInfo"></div>
 *   <input type="hidden" data-baby-select-target="hiddenInput" name="baby_id">
 * </div>
 *
 * 事件：
 * - baby-select:selected - 选择宝宝时触发，detail 包含宝宝信息
 */
export default class extends Controller {
  static targets = ["input", "dropdown", "selectedInfo", "hiddenInput"]
  static values = {
    url: { type: String, default: "/admin/ai/playground/babies" },
    selected: { type: Object, default: null },
    // i18n translations
    noDataText: { type: String, default: "暂无数据" },
    monthsText: { type: String, default: "月" },
    maleText: { type: String, default: "男" },
    femaleText: { type: String, default: "女" },
    viewText: { type: String, default: "查看 →" }
  }

  connect() {
    this.isOpen = false
    this.babies = []
    this.searchTimeout = null
    this.loadInitialBabies()
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    document.removeEventListener("click", this.handleOutsideClick)
  }

  // 加载默认宝宝列表
  async loadInitialBabies() {
    try {
      const response = await fetch(`${this.urlValue}?limit=10`)
      if (response.ok) {
        this.babies = await response.json()
      }
    } catch (error) {
      console.error("Failed to load babies:", error)
    }
  }

  // 输入框聚焦时打开下拉
  onFocus() {
    this.openDropdown()
  }

  // 输入搜索
  onInput(event) {
    const query = event.target.value.trim()

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    // 延迟搜索，避免频繁请求
    this.searchTimeout = setTimeout(() => {
      this.search(query)
    }, 200)
  }

  // 搜索宝宝
  async search(query) {
    try {
      const url = query
        ? `${this.urlValue}?q=${encodeURIComponent(query)}&limit=10`
        : `${this.urlValue}?limit=10`

      const response = await fetch(url)
      if (response.ok) {
        this.babies = await response.json()
        this.renderDropdown()
      }
    } catch (error) {
      console.error("Search failed:", error)
    }
  }

  // 打开下拉菜单
  openDropdown() {
    if (this.isOpen) return
    this.isOpen = true
    this.renderDropdown()
    this.dropdownTarget.classList.remove("hidden")

    // 添加点击外部关闭
    this.handleOutsideClick = (e) => {
      if (!this.element.contains(e.target)) {
        this.closeDropdown()
      }
    }
    setTimeout(() => {
      document.addEventListener("click", this.handleOutsideClick)
    }, 0)
  }

  // 关闭下拉菜单
  closeDropdown() {
    this.isOpen = false
    this.dropdownTarget.classList.add("hidden")
    document.removeEventListener("click", this.handleOutsideClick)
  }

  // 渲染下拉菜单
  renderDropdown() {
    if (this.babies.length === 0) {
      this.dropdownTarget.innerHTML = `
        <div class="p-3 text-sm text-zinc-500 dark:text-zinc-400">
          ${this.escapeHtml(this.noDataTextValue)}
        </div>
      `
      return
    }

    const items = this.babies.map(baby => `
      <button type="button"
              class="w-full px-3 py-2 text-left hover:bg-zinc-100 dark:hover:bg-zinc-700 flex items-center justify-between"
              data-action="click->baby-select#selectBaby"
              data-baby-id="${this.escapeAttr(baby.id)}"
              data-baby-name="${this.escapeAttr(baby.name)}"
              data-baby-age="${baby.age_months}"
              data-baby-family="${this.escapeAttr(baby.family_name)}">
        <div>
          <span class="font-medium text-zinc-900 dark:text-white">${this.escapeHtml(baby.name)}</span>
          <span class="text-zinc-500 dark:text-zinc-400 text-sm ml-2">${baby.age_months}${this.escapeHtml(this.monthsTextValue)}</span>
        </div>
        <span class="text-xs text-zinc-400 dark:text-zinc-500">${this.escapeHtml(baby.family_name)}</span>
      </button>
    `).join("")

    this.dropdownTarget.innerHTML = items
  }

  // 选择宝宝
  selectBaby(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const baby = {
      id: btn.dataset.babyId,
      name: btn.dataset.babyName,
      age_months: parseFloat(btn.dataset.babyAge),
      family_name: btn.dataset.babyFamily
    }

    this.selectedValue = baby
    this.inputTarget.value = baby.name

    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = baby.id
    }

    this.renderSelectedInfo(baby)
    this.closeDropdown()

    // 触发自定义事件
    this.dispatch("selected", { detail: baby })
  }

  // 渲染选中信息
  renderSelectedInfo(baby) {
    if (!this.hasSelectedInfoTarget) return

    const gender = baby.gender === 'male' ? this.maleTextValue : baby.gender === 'female' ? this.femaleTextValue : ''
    const details = `${baby.age_months}${this.monthsTextValue}${gender ? ' · ' + gender : ''} · ${baby.family_name}`

    this.selectedInfoTarget.innerHTML = `
      <div class="flex items-center justify-between">
        <div>
          <span class="font-medium text-blue-900 dark:text-blue-100">${this.escapeHtml(baby.name)}</span>
          <span class="text-sm text-blue-700 dark:text-blue-300 ml-2">${this.escapeHtml(details)}</span>
        </div>
        <a href="/admin/babies/${baby.id}" class="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
          ${this.escapeHtml(this.viewTextValue)}
        </a>
      </div>
    `
    this.selectedInfoTarget.classList.remove("hidden")
  }

  // 清除选择
  clear() {
    this.selectedValue = null
    this.inputTarget.value = ""
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = ""
    }
    if (this.hasSelectedInfoTarget) {
      this.selectedInfoTarget.classList.add("hidden")
    }
    this.dispatch("cleared")
  }

  // 获取当前选中的宝宝ID
  getSelectedId() {
    return this.selectedValue?.id || null
  }

  // HTML 转义
  escapeHtml(text) {
    if (text == null) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  // 属性转义
  escapeAttr(text) {
    if (text == null) return ""
    return String(text)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }
}
